// Evaluerar Atom[] till numeriskt värde. Direkt rekursiv evaluator —
// uttrycket är redan strukturerat (sup/frac/paren har explicita slots),
// så vi behöver bara hantera +,-,*,/ med precedens i den platta listan.

import type { Atom, Mode } from "./types";

export class CalcError extends Error {}

interface Ctx {
  mode: Mode;
}

export function evalRoot(root: Atom[], mode: Mode): number {
  if (root.length === 0) return 0;
  return parseAddSub(root, { i: 0 }, { mode });
}

interface Pos {
  i: number;
}

// Implicit multiplication: när två operand-aktiga atomer ligger bredvid
// varandra utan op-atom emellan, multiplicera dem. Triggas av t.ex.
// 2π, 3(x+1), sin(x)cos(x).
function isOperandAtom(a: Atom): boolean {
  switch (a.kind) {
    case "num":
    case "const":
    case "paren":
    case "abs":
    case "sqrt":
    case "nthroot":
    case "frac":
    case "sup":
    case "fn":
    case "logbase":
    case "fact":
    case "binom":
      return true;
    case "op":
    case "comma":
      return false;
  }
}

function parseAddSub(list: Atom[], pos: Pos, ctx: Ctx): number {
  let lhs = parseMulDiv(list, pos, ctx);
  while (pos.i < list.length) {
    const a = list[pos.i];
    if (a.kind !== "op" || (a.v !== "+" && a.v !== "-")) break;
    pos.i += 1;
    const rhs = parseMulDiv(list, pos, ctx);
    lhs = a.v === "+" ? lhs + rhs : lhs - rhs;
  }
  return lhs;
}

function parseMulDiv(list: Atom[], pos: Pos, ctx: Ctx): number {
  let lhs = parseUnary(list, pos, ctx);
  while (pos.i < list.length) {
    const a = list[pos.i];
    // Explicit * eller /
    if (a.kind === "op" && (a.v === "*" || a.v === "/")) {
      pos.i += 1;
      const rhs = parseUnary(list, pos, ctx);
      lhs = a.v === "*" ? lhs * rhs : safeDiv(lhs, rhs);
      continue;
    }
    // Implicit multiplikation
    if (isOperandAtom(a)) {
      const rhs = parseUnary(list, pos, ctx);
      lhs = lhs * rhs;
      continue;
    }
    break;
  }
  return lhs;
}

function parseUnary(list: Atom[], pos: Pos, ctx: Ctx): number {
  // Hantera prefix "-" och "+" — betraktas som unary om det är första
  // atom i sin nivå eller följer en op (men i vår implementation är detta
  // endast aktuellt vid första iterationen).
  if (pos.i < list.length) {
    const a = list[pos.i];
    if (a.kind === "op" && (a.v === "+" || a.v === "-")) {
      pos.i += 1;
      const v = parseUnary(list, pos, ctx);
      return a.v === "-" ? -v : v;
    }
  }
  return parseAtom(list, pos, ctx);
}

function parseAtom(list: Atom[], pos: Pos, ctx: Ctx): number {
  if (pos.i >= list.length) throw new CalcError("oväntat slut");
  const a = list[pos.i];
  pos.i += 1;
  switch (a.kind) {
    case "num": {
      const n = Number(a.v);
      if (!isFinite(n)) throw new CalcError("ogiltigt tal");
      return n;
    }
    case "const":
      return a.name === "pi" ? Math.PI : Math.E;
    case "paren":
      if (a.inner.length === 0) throw new CalcError("tom parentes");
      return evalRoot(a.inner, ctx.mode);
    case "abs":
      return Math.abs(evalRoot(a.inner, ctx.mode));
    case "sqrt": {
      const x = evalRoot(a.inner, ctx.mode);
      if (x < 0) throw new CalcError("√ av negativt");
      return Math.sqrt(x);
    }
    case "nthroot": {
      const n = evalRoot(a.degree, ctx.mode);
      const x = evalRoot(a.inner, ctx.mode);
      if (n === 0) throw new CalcError("0:e roten odefinierad");
      // För negativa argument tillåt udda heltal-rötter
      if (x < 0) {
        const isOddInt = Number.isInteger(n) && n % 2 !== 0;
        if (!isOddInt) throw new CalcError("rot av negativt");
        return -Math.pow(-x, 1 / n);
      }
      return Math.pow(x, 1 / n);
    }
    case "frac": {
      const num = evalRoot(a.num, ctx.mode);
      const den = evalRoot(a.den, ctx.mode);
      return safeDiv(num, den);
    }
    case "sup": {
      const base = evalRoot(a.base, ctx.mode);
      const exp = evalRoot(a.exp, ctx.mode);
      const r = Math.pow(base, exp);
      if (!Number.isFinite(r)) {
        // (-1)^0.5, 0^(-1), 1/0 etc → NaN/Infinity
        if (Number.isNaN(r)) throw new CalcError("ogiltig potens");
        throw new CalcError("potens för stor");
      }
      return r;
    }
    case "fn": {
      const x = evalRoot(a.arg, ctx.mode);
      return applyFn(a.name, x, ctx.mode);
    }
    case "logbase": {
      const base = evalRoot(a.base, ctx.mode);
      const arg = evalRoot(a.arg, ctx.mode);
      if (arg <= 0) throw new CalcError("log av icke-positivt");
      if (base <= 0 || base === 1) throw new CalcError("ogiltig logbas");
      return Math.log(arg) / Math.log(base);
    }
    case "fact": {
      const x = evalRoot(a.inner, ctx.mode);
      if (!Number.isInteger(x) || x < 0) {
        throw new CalcError("fakultet kräver heltal ≥ 0");
      }
      if (x > 170) throw new CalcError("fakultet för stor");
      let r = 1;
      for (let i = 2; i <= x; i++) r *= i;
      return r;
    }
    case "binom": {
      const n = evalRoot(a.n, ctx.mode);
      const r = evalRoot(a.r, ctx.mode);
      if (
        !Number.isInteger(n) ||
        !Number.isInteger(r) ||
        n < 0 ||
        r < 0 ||
        r > n
      ) {
        throw new CalcError(
          a.binomTyp === "ncr"
            ? "nCr kräver heltal med n ≥ r ≥ 0"
            : "nPr kräver heltal med n ≥ r ≥ 0",
        );
      }
      const fact = (k: number) => {
        if (k > 170) throw new CalcError("nCr/nPr för stora tal");
        let p = 1;
        for (let i = 2; i <= k; i++) p *= i;
        return p;
      };
      // Använd multiplikativ formel för bättre precision på stora värden:
      // C(n,r) = (n·(n-1)·…·(n-r+1)) / r!  — undviker att räkna n! direkt
      // och bevarar exakta heltal upp till JS Number-precision (~2^53).
      if (a.binomTyp === "ncr") {
        // Symmetri: C(n,r) = C(n, n-r). Välj minsta r för effektivitet.
        const k = Math.min(r, n - r);
        let res = 1;
        for (let i = 0; i < k; i++) res = (res * (n - i)) / (i + 1);
        if (!Number.isFinite(res)) throw new CalcError("nCr för stort");
        return Math.round(res);
      }
      // nPr = n·(n-1)·…·(n-r+1)
      let res = 1;
      for (let i = 0; i < r; i++) res *= n - i;
      if (!Number.isFinite(res)) throw new CalcError("nPr för stort");
      return res;
    }
    case "op":
    case "comma":
      throw new CalcError("oväntad operator");
  }
}

function safeDiv(a: number, b: number): number {
  if (b === 0) throw new CalcError("division med noll");
  return a / b;
}

function applyFn(
  name: "sin" | "cos" | "tan" | "asin" | "acos" | "atan" | "ln" | "lg" | "log",
  x: number,
  mode: Mode,
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;
  // Floating-point cleanup: värden inom 1e-12 från heltal/0 betraktas som
  // det heltalet. sin(180°) ska vara 0, inte 1.22e-16. Bara aktuellt för
  // sin/cos eftersom de matematiskt har exakta nollställen vid heltals-
  // multiplar av π/180°.
  const cleanZero = (v: number): number =>
    Math.abs(v) < 1e-12 ? 0 : Math.abs(v - Math.round(v)) < 1e-12 ? Math.round(v) : v;
  switch (name) {
    case "sin":
      return cleanZero(Math.sin(mode === "deg" ? toRad(x) : x));
    case "cos":
      return cleanZero(Math.cos(mode === "deg" ? toRad(x) : x));
    case "tan": {
      // tan har asymptoter vid 90°+k·180° (cos = 0). Math.tan ger då
      // ett enormt tal istället för error. Vi kollar |cos| ~ 0 och kastar.
      const rad = mode === "deg" ? toRad(x) : x;
      const c = Math.cos(rad);
      if (Math.abs(c) < 1e-12) throw new CalcError("tan odefinierad");
      return cleanZero(Math.tan(rad));
    }
    case "asin":
      if (x < -1 || x > 1) throw new CalcError("sin⁻¹ utanför [-1,1]");
      return mode === "deg" ? toDeg(Math.asin(x)) : Math.asin(x);
    case "acos":
      if (x < -1 || x > 1) throw new CalcError("cos⁻¹ utanför [-1,1]");
      return mode === "deg" ? toDeg(Math.acos(x)) : Math.acos(x);
    case "atan":
      return mode === "deg" ? toDeg(Math.atan(x)) : Math.atan(x);
    case "ln":
      if (x <= 0) throw new CalcError("ln av icke-positivt");
      return Math.log(x);
    case "lg":
      if (x <= 0) throw new CalcError("lg av icke-positivt");
      return Math.log10(x);
    case "log":
      // "log" utan bas tolkas som lg (Sverige-standard)
      if (x <= 0) throw new CalcError("log av icke-positivt");
      return Math.log10(x);
  }
}

const MAX_DIGITS = 12;

export function formatNumber(n: number): string {
  if (!isFinite(n)) return "Fel";
  const abs = Math.abs(n);
  if (abs !== 0 && (abs < 1e-6 || abs >= 1e12)) {
    return n.toExponential(6);
  }
  return String(Number(n.toPrecision(MAX_DIGITS)));
}
